export const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
export const isString = (value) => typeof value === 'string';
export const isBoolean = (value) => typeof value === 'boolean';
export const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);
export const isNullableString = (value) => value === null || isString(value);
export const isStringArray = (value) => Array.isArray(value) && value.every(isString);
export const isGuardedArgument = (value) => isRecord(value) && isString(value.name) && 'value' in value && typeof value.guard === 'function';
export const assertArguments = (argumentsToCheck) => {
    for (const argument of argumentsToCheck) {
        if (!isGuardedArgument(argument) || !argument.guard(argument.value)) {
            throw new TypeError(`Invalid argument: ${isRecord(argument) ? String(argument.name) : 'unknown'}`);
        }
    }
};
export const assertType = (input) => {
    if (!isGuardedArgument(input) || !input.guard(input.value)) {
        throw new TypeError(`Invalid ${isRecord(input) ? String(input.name) : 'value'}`);
    }
    return input.value;
};
export const isPlatformRole = (value) => value === 'USER' || value === 'SUPERADMIN';
export const isIntegrationStatus = (value) => ['NOT_CONFIGURED', 'PROVISIONING', 'READY', 'DEGRADED', 'ERROR'].includes(String(value));
export const isCallStatus = (value) => [
    'CREATED',
    'AUTHORIZING',
    'DIALING',
    'RINGING',
    'CONNECTED',
    'COMPLETED',
    'FAILED',
    'CANCELED',
].includes(String(value));
export const isApiError = (value) => isRecord(value) &&
    isString(value.code) &&
    isString(value.message) &&
    isString(value.requestId) &&
    (value.fieldErrors === undefined ||
        (isRecord(value.fieldErrors) && Object.values(value.fieldErrors).every(isString)));
export const isSessionUser = (value) => isRecord(value) &&
    isString(value.id) &&
    isString(value.email) &&
    isString(value.displayName) &&
    isNullableString(value.avatarUrl) &&
    isPlatformRole(value.role) &&
    isString(value.tenantId);
export const isSessionResponse = (value) => isRecord(value) &&
    isBoolean(value.authenticated) &&
    (value.user === null || isSessionUser(value.user));
export const isTenantSettings = (value) => isRecord(value) && isString(value.defaultCountry) && isNullableString(value.defaultCallerId);
export const isUpdateTenantSettingsRequest = (value) => isTenantSettings(value);
export const isIntegrationSummary = (value) => isRecord(value) &&
    isIntegrationStatus(value.status) &&
    isNullableString(value.maskedAccount) &&
    isNullableString(value.updatedAt) &&
    isNullableString(value.message);
export const isVoxServiceAccountInput = (value) => isRecord(value) &&
    isNumber(value.account_id) &&
    Number.isInteger(value.account_id) &&
    isString(value.key_id) &&
    isString(value.private_key) &&
    value.private_key.includes('PRIVATE KEY');
export const isConfigureIntegrationRequest = (value) => isRecord(value) &&
    isVoxServiceAccountInput(value.serviceAccount) &&
    isString(value.accountName) &&
    isString(value.node);
export const isCallerIdSummary = (value) => isRecord(value) &&
    isString(value.id) &&
    isString(value.label) &&
    isString(value.phoneNumber) &&
    isBoolean(value.enabled);
export const isCreateCallRequest = (value) => isRecord(value) && isString(value.destination) && isString(value.callerIdId);
export const isCallIntentResponse = (value) => isRecord(value) &&
    isString(value.callId) &&
    isString(value.intentToken) &&
    isString(value.expiresAt) &&
    isString(value.voxUsername) &&
    isString(value.voxNode) &&
    isString(value.route);
export const isCallSummary = (value) => isRecord(value) &&
    isString(value.id) &&
    isCallStatus(value.status) &&
    isString(value.destination) &&
    isString(value.callerId) &&
    isString(value.startedAt) &&
    isNullableString(value.connectedAt) &&
    isNullableString(value.endedAt) &&
    isNumber(value.connectedSeconds) &&
    isNumber(value.billedSeconds) &&
    isString(value.cost) &&
    isString(value.currency) &&
    isNullableString(value.failureReason);
export const isPaginatedCalls = (value) => isRecord(value) &&
    Array.isArray(value.items) &&
    value.items.every(isCallSummary) &&
    isNullableString(value.nextCursor);
export const isUsageSummary = (value) => isRecord(value) &&
    isNumber(value.callCount) &&
    isNumber(value.connectedCount) &&
    isNumber(value.connectedMinutes) &&
    isNumber(value.billedMinutes) &&
    isString(value.cost) &&
    isString(value.currency);
export const isAdminUserUsage = (value) => isRecord(value) &&
    isString(value.userId) &&
    isString(value.email) &&
    isString(value.displayName) &&
    isPlatformRole(value.role) &&
    isBoolean(value.enabled) &&
    isString(value.registeredAt) &&
    isNullableString(value.lastLoginAt) &&
    isIntegrationStatus(value.integrationStatus) &&
    Array.isArray(value.usage) &&
    value.usage.every(isUsageSummary);
export const isRoleChangeRequest = (value) => isRecord(value) && isString(value.userId) && isPlatformRole(value.role);
export const isAudioDevicePreferences = (value) => isRecord(value) &&
    isNullableString(value.inputId) &&
    isNullableString(value.outputId) &&
    isBoolean(value.echoCancellation) &&
    isBoolean(value.noiseSuppression) &&
    isBoolean(value.autoGainControl);
export const isScenarioAuthorizeRequest = (value) => isRecord(value) && isString(value.callId) && isString(value.intentToken);
export const isScenarioAuthorizeResponse = (value) => isRecord(value) &&
    isString(value.destination) &&
    isString(value.callerId) &&
    isString(value.callbackToken);
export const isScenarioEventRequest = (value) => isRecord(value) &&
    isString(value.callId) &&
    isString(value.callbackToken) &&
    ['DIALING', 'RINGING', 'CONNECTED', 'COMPLETED', 'FAILED'].includes(String(value.event)) &&
    (value.reason === undefined || isString(value.reason));
export const isMeResponse = (value) => isRecord(value) &&
    isSessionUser(value.user) &&
    isTenantSettings(value.settings) &&
    isIntegrationSummary(value.integration);
export const isAdminHealth = (value) => isRecord(value) &&
    isNumber(value.pendingJobs) &&
    isNumber(value.failedJobs) &&
    isNumber(value.reconciliationLag);
//# sourceMappingURL=index.js.map